import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Trash2, UserX, RefreshCw, Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import SmartPagination from '@/components/SmartPagination';
import { admin } from '@/lib/api';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

const STATUS_LABELS = {
  active: 'Aktif',
  unsubscribed: 'Abonelik iptal',
};

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-800',
  unsubscribed: 'bg-slate-100 text-slate-700',
};

function formatDateTimeTr(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const NewsletterSubscriptionsTab = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await admin.newsletterSubscriptionsList(params);
      setRows(res.data.subscribers || []);
      setTotal(res.data.total || 0);
      setActiveCount(res.data.active_count ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Abonelikler yüklenemedi');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleUnsubscribe = async (id) => {
    try {
      setActionLoading(true);
      await admin.newsletterUnsubscribe(id);
      toast.success('Abonelik iptal edildi');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setActionLoading(true);
      await admin.newsletterDelete(deleteTarget.id);
      toast.success('Kayıt silindi');
      setDeleteTarget(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-subscriptions-admin">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Bülten Abonelikleri</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Footer bülten formundan gelen e-posta abonelikleri
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Toplam kayıt (filtreli)</CardDescription>
            <CardTitle className="text-3xl">{total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Aktif abone</CardDescription>
            <CardTitle className="text-3xl text-green-700">{activeCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Abone listesi
          </CardTitle>
          <CardDescription>E-posta, dil, kaynak ve durum</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex flex-1 gap-2">
              <Input
                type="search"
                placeholder="E-posta ara..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="max-w-sm"
              />
              <Button type="submit" variant="secondary" size="icon" aria-label="Ara">
                <Search className="h-4 w-4" />
              </Button>
            </form>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="unsubscribed">İptal edilmiş</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchList} aria-label="Yenile">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">Henüz abonelik yok.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Dil</TableHead>
                  <TableHead>Kaynak</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Kayıt tarihi</TableHead>
                  <TableHead>Onay maili</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.email}</TableCell>
                    <TableCell>{row.locale || '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{row.source || 'footer'}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_BADGE[row.status] || STATUS_BADGE.active} variant="secondary">
                        {STATUS_LABELS[row.status] || row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDateTimeTr(row.created_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {row.welcome_email_sent_at ? formatDateTimeTr(row.welcome_email_sent_at) : '—'}
                      {row.unsubscribed_at && (
                        <span className="block text-amber-700">
                          İptal: {formatDateTimeTr(row.unsubscribed_at)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {row.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() => handleUnsubscribe(row.id)}
                          title="Aboneliği iptal et"
                        >
                          <UserX className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => setDeleteTarget(row)}
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > 0 && (
            <SmartPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={total}
              pageSize={PAGE_SIZE}
            />
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aboneliği sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.email}</strong> kaydı kalıcı olarak silinecek. Emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={actionLoading}>
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewsletterSubscriptionsTab;
